/**
 * `R4-UIE1` · Borde HTTP de la pantalla «Exportación».
 *
 * La pantalla del Management Tool habla SOLO con `apps/admin-web` por HTTP y no
 * guarda estado (`arquitectura.html` §1: «no hay proceso que operar: es un
 * bucket»). Este archivo es el ÚNICO de `exportacion/` que emite `fetch`
 * (guard ①): la LISTA paginada, el listado de LOTES del filtro y la DESCARGA del
 * CSV, con la forma que `contracts/analytics-api/http.ts` declara.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⟦rastro · `ADR-0082` · `R4-UIE1`⟧ ① B10 · **YA NO ENTRA
 * `contracts/exports-api`**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hasta R4 este archivo importaba cinco tipos de `@contracts/exports-api/http.ts`
 * y uno de `@contracts/exports-api/index.ts`, y declaraba `historial`, `lotes`,
 * `descargar(id)` y `generar(lotes)` — la cola de F5. **Las dos operaciones de la
 * cola se retiran y van comentadas más abajo, no borradas**: `R4-OPS-A` apaga la
 * exportación vieja y exige **0 importadores** de ese contrato para poder
 * comentarlo; con el import puesto, la SPA no compila el día que se comente.
 *
 * ⇒ hoy:
 *   · la lista y la descarga salen de `contracts/analytics-api` (`R4-CAP1`,
 *     `EXPORTACION_PATH` y `EXPORTACION_DESCARGA_SEGMENT`), y
 *   · los LOTES salen del tipo que `R4-CPA1` mudó a
 *     `contracts/participants/http.ts` (`ExportLotesPath`,
 *     `ExportLotesResponseBody`) **con la MISMA ruta en el cable**
 *     (`exportaciones/lotes`, B10: «se conserva igual en el cable, servida desde
 *     `participants`»). El plural viejo NO se reusa para la lista: el camino de
 *     la lista es `{brazo}/exportacion` y `{brazo}/exportaciones` tiene que dar
 *     404 (`contracts/analytics-api/http.ts` ⟦las-rutas-de-exportacion⟧).
 *
 * ⚠️ **El nombre del tipo exportado y la ruta de este archivo NO cambian**
 * (`BordeExportacion` en `src/exportacion/borde-exportacion.ts`), y no es una
 * casualidad: `src/contexto-borde-exportacion.tsx` es paramétrico en el tipo y
 * **sólo nombra el tipo y la ruta** (medido en `r4-uie1.md` §«El hunk condicional
 * del proveedor»). Renombrar el tipo o mover el archivo haría falta tocar el
 * proveedor, que es otro territorio.
 *
 * ⚠️ **`lotes()` conserva su firma EXACTA** (`() => Promise<readonly string[]>`)
 * porque **no la consume sólo esta pantalla**: `App.tsx:2515` arma el borde del
 * padrón con `crearBordePadronHttp(config, bordeExportacion.lotes)` —la misma
 * función que usan Participantes, Alta manual y el filtro de Nuevo envío—.
 * `App.tsx` es territorio de plan B (X7) y no se toca acá: angostarle el tipo o
 * renombrarla rompería tres pantallas que esta etapa no tiene permitido tocar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② LO QUE ENTRA DEL REPO SIGUEN SIENDO SÓLO TIPOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Misma regla que `borde-envios.ts`, `borde-metricas.ts` y
 * `borde-participantes.ts`: los imports de `contracts/**` son `import type`. El
 * alias `@contracts/*` vive en `tsconfig.json` y NO en `vite.config.ts`, así que
 * importar un VALOR de un contrato haría fallar el build. Por eso las rutas se
 * repiten acá como literales, **tipados contra los tipos de path del contrato**:
 * el día que una ruta cambie en el contrato, este build se cae en vez de seguir
 * hablándole a un endpoint que no existe.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ③ NI EL BRAZO NI EL ACTOR NI LA CAPACIDAD CRUZAN POR EL NAVEGADOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `contracts/analytics-api/http.ts` ⟦ni-arm-ni-actor-en-el-cable⟧: no hay ruta,
 * ni query param, ni campo de cuerpo por donde el cliente elija el brazo o el
 * actor. El brazo va ADELANTE de la ruta (`raizDelBrazo`) y `admin-web` lo
 * resuelve de la sesión autenticada (`ADR-0006`).
 *
 * ⛔ **Y la CAPACIDAD tampoco viaja.** Las 14 o 12 columnas las decide
 * `admin-web` con la declaración del brazo (`exportacion-de-participantes.ts`
 * §`capacidadDe`), no el navegador: un `?capacidad=` sería el cliente eligiendo
 * si el archivo lleva «Pidió humano». La pantalla la vuelve a leer de la MISMA
 * declaración para saber cuántas columnas dibujar (`ADR-0318` / B16), pero no la
 * manda.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ④ LA DESCARGA DEJÓ DE NAVEGAR, Y ES LA MITAD QUE CA-77/CA-79 NECESITAN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Acá había `window.location.assign(...)`: el navegador bajaba el archivo por su
 * cuenta, el JavaScript **no se enteraba de un 500** y no podía mostrar
 * «Descargando…» mientras el CSV se armaba. `descargar` ahora devuelve una
 * `Promise` y delega en `descargarArchivo` (la primitiva de `R4-UI0`, A6): `fetch`
 * con credenciales → `!ok` lanza → `Blob` → nombre de `Content-Disposition` →
 * `<a download>`.
 *
 * ⛔ **El nombre del archivo NO se arma acá y no hay nombre de respaldo.** Lo pone
 * el servidor (regla 17, `R4-AWE1`) y la consola lo lee de `Content-Disposition`
 * —que `R4-AWC0` expone por CORS—. Un `"exportacion.csv"` de respaldo elegiría un
 * nombre sin instancia ni rango y dejaría pasar un `Expose-Headers` borrado.
 */

import type {
  ExportacionDescargaPath,
  ExportacionPath,
  ExportacionResponseBody,
} from "@contracts/analytics-api/http.ts"
import type { AnalyticsManagementApi } from "@contracts/analytics-api/index.ts"
import type { ExportLotesPath, ExportLotesResponseBody } from "@contracts/participants/http.ts"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { descargarArchivo } from "../descargar-archivo.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"
import { raizDelBrazo } from "../brazo-del-cable.ts"

/*
 * ⟦rastro · `ADR-0082` · `R4-UIE1`⟧ **Acá vivían los tipos de la cola de F5**, y
 * se dejan dichos en vez de borrados porque describen lo que la pantalla HACÍA:
 *
 *   type ExportId        = Parameters<ExportsManagementApi["download"]>[1]
 *   type ExportListQuery = Parameters<ExportsManagementApi["history"]>[1]
 *   type LoteId          = ExportTriggerRequestBody[number]
 *
 * Los tres se derivaban de `contracts/exports-api`, que esta etapa deja de
 * importar (B10). La fila del historial —`ExportHistoryResponseBody["items"][number]`,
 * con `id`/`lotes`/`generadaEn`/`registros`/`archivo`— tampoco existe más: la fila
 * de hoy es una PERSONA con sus columnas, no un archivo generado.
 */

/**
 * La consulta de la lista, **DERIVADA del puerto** y no importada de
 * `contracts/analytics` (el contrato de DOMINIO).
 *
 * Es `ExportQuery`: `lotes`, `desde`, `hasta` (los días de Chile, o `null`),
 * `page` base 1 y `pageSize`. Derivarla del puerto de la superficie mantiene UNA
 * sola fuente: un cambio en el dominio rompe la compilación acá igual que un
 * import directo.
 */
export type ConsultaDeExportacion = Parameters<AnalyticsManagementApi["exportacion"]>[2]

/**
 * El FILTRO sin paginado: lo que la lista y la descarga comparten.
 *
 * ⛔ Es un `Omit` y no un tipo escrito a mano: `apps/admin-web` lee el filtro de
 * las dos rutas con **una sola función** (`exportacion-de-participantes.ts`
 * §`leerFiltro`) justamente para que el archivo no pueda traer otras filas que la
 * tabla que el operador estaba mirando (CA-80). Dos declaraciones de este
 * subconjunto volverían a abrir esa puerta del lado del cliente.
 */
export type FiltroDeExportacion = Omit<ConsultaDeExportacion, "page" | "pageSize">

/**
 * Uno de los SEIS tamaños de página que el cable acepta
 * (`TamanoDePagina = 10 | 50 | 100 | 200 | 500 | 1000`).
 *
 * Se deriva en vez de escribirse: cualquier otro número es `400` en el borde
 * (`R4-F2` RT 4), así que un `number` acá sería la pantalla pudiendo pedir algo
 * que el servidor rechaza.
 */
export type TamanoDeExportacion = ConsultaDeExportacion["pageSize"]

/** La página de la lista, tal como viaja por el cable (`EnElCable<ExportPage>`). */
export type PaginaDeExportacion = ExportacionResponseBody

/**
 * La fila de Exportación en el cable: **una persona con sus 14 o 12 columnas**.
 *
 * ⛔ Es la UNIÓN de las dos variantes del contrato
 * (`FilaDeExportacionConDerivacion | FilaDeExportacionSinDerivacion`), y en la
 * segunda `pidioHumano` es `?: never` (`ADR-0318`): el tipo hace imposible que la
 * fila del Brazo Puro traiga la columna. **La pantalla no decide por la presencia
 * del campo** —eso sería decidir por el dato— sino por la capacidad declarada.
 */
export type FilaDeExportacion = PaginaDeExportacion["items"][number]

/**
 * El borde de exportación visto desde el navegador.
 *
 * Sigue la forma del contrato pero adapta las entradas que la pantalla puede
 * emitir: el `arm`, el `actor` y la `capacidad` no entran por acá — los resuelve
 * `admin-web` (`ADR-0006`, `ADR-0318`).
 */
export type BordeExportacion = {
  /**
   * `GET exportacion?lotes=…&desde=…&hasta=…&pagina=N&tamano=M`. La página es
   * base 1 y el `total` que vuelve es el total EN LA BASE, nunca `items.length`.
   */
  readonly lista: (consulta: ConsultaDeExportacion) => Promise<PaginaDeExportacion>
  /**
   * `GET exportaciones/lotes` (B10: la ruta vieja, servida desde `participants`).
   * El arreglo DESNUDO de nombres de lote del brazo, **en el orden que trae el
   * servidor**: del más reciente al más antiguo y, a igual fecha de creación, por
   * nombre (`modules/participants/src/lecturas.ts:440`). Un brazo sin lotes es
   * `[]`, que es un resultado y no un error.
   */
  readonly lotes: () => Promise<readonly string[]>
  /**
   * `GET exportacion/descarga?lotes=…&desde=…&hasta=…`. **Devuelve una `Promise`**
   * —no navega— para que la pantalla pueda deshabilitar el botón mientras dura
   * (CA-79) y mostrar su aviso si falla (CA-77). El nombre del archivo lo pone el
   * servidor.
   */
  readonly descargar: (filtro: FiltroDeExportacion) => Promise<void>
  /*
   * ⟦rastro · `ADR-0082` · `R4-UIE1`⟧ **Acá estaban `historial` y `generar`**, las
   * dos mitades de la cola de F5:
   *
   *   readonly historial: (query: ExportListQuery) => Promise<ExportHistoryResponseBody>
   *   readonly generar: (lotes: readonly LoteId[]) => Promise<void>
   *
   * `R4-F2` las retira del producto: *«La pantalla no tiene lista de exportaciones
   * generadas, ni botón “Generar”, ni avisos de exportación en cola: la tabla es lo
   * que se va a exportar y el archivo se arma al descargarlo»* (`PRD_R4.md:1166`),
   * y CA-05 lo afirma por la AUSENCIA. No se borran de la historia porque lo que
   * decían era cierto y `R4-OPS-A` todavía tiene que apagar el otro extremo.
   *
   * ⚠️ Y `descargar` cambió de forma, no sólo de cuerpo: era
   * `descargar: (id: ExportId) => void` —el id de un archivo ya generado, bajado
   * por navegación—. Hoy recibe el FILTRO aplicado y devuelve una promesa.
   */
}

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/**
 * El acuerdo de cable de la exportación, todo junto y en un solo lugar.
 *
 * Los valores literales repiten los del contrato; los tipos de path son del
 * contrato, así que una divergencia rompe el typecheck y no pasa desapercibida.
 */
const RUTA_EXPORTACION: ExportacionPath = "exportacion"

/**
 * `exportacion/descarga` — se escribe ENTERA como literal y se anota con
 * `ExportacionDescargaPath`, que es la forma que el contrato pide para ella:
 * **sin la anotación ésta sería la única ruta que puede quedar mal escrita en
 * producción sin que ningún build se caiga** —no la recorre ningún `fetch` cuyo
 * tipo la ate— y el día que el contrato le cambie el segmento, el `404` se
 * leería como «la exportación está rota».
 */
const RUTA_DESCARGA: ExportacionDescargaPath = "exportacion/descarga"

/** B10 · la ruta del selector de lotes **no cambia**, y su tipo ya es de `participants`. */
const RUTA_LOTES: ExportLotesPath = "exportaciones/lotes"

/*
 * Los nombres de los query params. **Van en castellano**, como el resto de la
 * herramienta, y son los mismos cinco que `contracts/analytics-api/http.ts`
 * declara como valores (`ANALYTICS_LOTES_PARAM`, `ANALYTICS_DESDE_PARAM`,
 * `ANALYTICS_HASTA_PARAM`, `ANALYTICS_PAGE_PARAM`, `ANALYTICS_PAGE_SIZE_PARAM`).
 *
 * ⚠️ **Se repiten como literales porque son VALORES del contrato y esta unidad no
 * puede importar valores** (docblock ②). El contrato no publica un tipo de
 * parámetro —sólo tipos de RUTA—, así que esta es la única parte del cable que
 * `tsc` no puede atar; lo que la sostiene es `gates/pantalla-exportacion.spec.ts`.
 */
const PARAMETRO_LOTES = "lotes"
const PARAMETRO_DESDE = "desde"
const PARAMETRO_HASTA = "hasta"
const PARAMETRO_PAGINA = "pagina"
const PARAMETRO_TAMANO = "tamano"

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

/**
 * Los tres valores que puede tomar una columna de estado
 * (`EstadoDeColumna = "Sí" | "No" | "—"`).
 *
 * ⛔ **Se valida en runtime y no se confía en el tipo**, porque es la diferencia
 * entre «dato» y «ausencia» que `contracts/analytics` declaró expresamente
 * (⟦el-guion-es-un-estado-no-un-texto⟧): un `string` libre en esta celda deja a la
 * pantalla adivinando qué es un «—» de verdad y qué es un texto que llegó roto.
 */
const ESTADOS_DE_COLUMNA: readonly string[] = ["Sí", "No", "—"]

function esEstado(valor: unknown): boolean {
  return typeof valor === "string" && ESTADOS_DE_COLUMNA.includes(valor)
}

/**
 * El motivo: `null` cuando «Recibió» dice «Sí», y si no `{ causa, texto }` con la
 * frase YA ARMADA por el servidor.
 *
 * ⛔ **Acá no hay tabla de frases.** La arma `modules/analytics`
 * (`exportacion-de-participantes/filas.ts` §`FRASES_DEL_MOTIVO` y la de «Fallido:»
 * con `reasonFor` de `contracts/channel`). Una segunda tabla de motivos en la
 * pantalla sería la que divergiera, y el CSV —que arma el servidor— dejaría de
 * coincidir con la tabla.
 */
function esMotivo(valor: unknown): boolean {
  if (valor === null) return true
  if (!esObjeto(valor)) return false
  return typeof valor["causa"] === "string" && typeof valor["texto"] === "string"
}

/**
 * Comprueba en runtime lo que el tipo promete y `response.json()` no puede saber.
 *
 * ⛔⛔ **DEVUELVE EL OBJETO ORIGINAL (predicado) EN VEZ DE RECONSTRUIRLO, Y ESO ES
 * LA DECISIÓN.** La fila es una UNIÓN con `pidioHumano?: never` en una de las dos
 * ramas y el proyecto corre con `exactOptionalPropertyTypes`: reconstruir el
 * literal obligaría a elegir una rama —o a escribir `pidioHumano: undefined`, que
 * con esa opción **no es lo mismo que ausente**— y la elección de rama es
 * exactamente la decisión que `ADR-0318` saca de la fila y pone en la CAPACIDAD.
 * Validar y devolver lo que llegó deja el dato intacto y la decisión en un solo
 * lugar.
 *
 * ⚠️ `pidioHumano` se valida **sólo si viene**: su ausencia es legítima (es lo que
 * manda el Brazo Puro) y su presencia no autoriza a nadie a dibujar la columna.
 */
function esFila(valor: unknown): valor is FilaDeExportacion {
  if (!esObjeto(valor)) return false

  for (const campo of ["id", "nombre", "telefono", "lote"]) {
    if (typeof valor[campo] !== "string") return false
  }
  for (const campo of [
    "sinInteraccionFechaHora",
    "siguioConChatbotFechaHora",
    "optOutFechaHora",
  ]) {
    if (typeof valor[campo] !== "string") return false
  }
  for (const campo of [
    "recibioMensajeInductorEnElRango",
    "sinInteraccion",
    "siguioConChatbot",
    "optOut",
  ]) {
    if (!esEstado(valor[campo])) return false
  }
  if (!esMotivo(valor["motivo"])) return false

  if (valor["pidioHumano"] !== undefined) {
    if (!esEstado(valor["pidioHumano"])) return false
    if (typeof valor["pidioHumanoFechaHora"] !== "string") return false
  }

  return true
}

/** Valida una página de la lista: `items` + el `total` de la base. */
function leerPagina(valor: unknown): PaginaDeExportacion | null {
  if (!esObjeto(valor)) return null

  const itemsCrudo = valor["items"]
  if (!Array.isArray(itemsCrudo)) return null
  if (!itemsCrudo.every((item) => esFila(item))) return null

  const total = valor["total"]
  if (typeof total !== "number" || !Number.isInteger(total) || total < 0) return null

  return { items: itemsCrudo as readonly FilaDeExportacion[], total }
}

/** Valida el listado de lotes: un arreglo desnudo de strings (B10). */
function leerLotes(valor: unknown): ExportLotesResponseBody | null {
  if (!Array.isArray(valor)) return null
  if (!valor.every((lote) => typeof lote === "string")) return null
  return valor
}

/**
 * Los parámetros del FILTRO, que la lista y la descarga arman IGUAL.
 *
 * ⛔ **`lotes` es REPETIBLE y no separado por comas** (`?lotes=A&lotes=B`), igual
 * que lo lee `apps/admin-web` (`exportacion-de-participantes.ts` §`leerFiltro`):
 * un nombre de lote puede tener una coma —«Exp Coma, H»— así que el separador por
 * comas perdería lotes en silencio, y sin fallar.
 *
 * ⛔ **Un lote AUSENTE no es un lote VACÍO.** Sin lotes elegidos no se escribe
 * `?lotes=`: el parámetro no va. Un `?lotes=` vacío es `400` del otro lado, y
 * tratarlo como «todos» le devolvería el brazo entero a quien quiso filtrar.
 *
 * ⛔⛔ **Los días viajan como TEXTO `aaaa-mm-dd` y nunca por `new Date()`.** Es el
 * defecto que R4 viene a arreglar: `new Date("2026-09-21").toISOString()` es
 * medianoche **UTC**, así que con un navegador al oeste de Greenwich el día que
 * llega al servidor es OTRO. El límite del día lo decide el servidor, en hora de
 * Chile (`B13`, `R4-AN1`); acá el día es una cadena y se pasa tal cual.
 */
function parametrosDelFiltro(filtro: FiltroDeExportacion): URLSearchParams {
  const parametros = new URLSearchParams()
  for (const lote of filtro.lotes) {
    parametros.append(PARAMETRO_LOTES, lote)
  }
  if (filtro.desde !== null) parametros.set(PARAMETRO_DESDE, filtro.desde)
  if (filtro.hasta !== null) parametros.set(PARAMETRO_HASTA, filtro.hasta)
  return parametros
}

/**
 * Arma el borde de exportación contra la `admin-web` configurada.
 *
 * Función y no módulo con estado, por lo mismo que `crearBordeMetricasHttp`: la
 * dirección llega en tiempo de ejecución (`configuracion.json`) y no hay ningún
 * momento de import en el que conocerla.
 */
export function crearBordeExportacionHttp(config: ConfiguracionInstancia): BordeExportacion {
  /**
   * ⭐ `AR5-m` · ⟦el-brazo-entra-por-ruta⟧ · **la raíz lleva el brazo adelante**, y
   * es una FUNCIÓN y no una constante porque el brazo sale de la dirección, que
   * cambia sin que este borde se vuelva a construir (`App.tsx` lo memoiza por
   * `config`). Ver `brazo-del-cable.ts`, que tiene la decisión del dueño citada.
   */
  const raiz = (): string => raizDelBrazo(config.adminWebUrl)

  /**
   * `credentials: "include"` en los dos pedidos de JSON, y ningún `Authorization`
   * armado acá. La sesión la maneja `admin-web` (Better Auth + MFA, `ADR-0006`) en
   * una cookie `HttpOnly` que el navegador envía solo: un token que el JavaScript
   * tuviera que leer para ponerlo en un header es un token que la pantalla tuvo
   * que GUARDAR, y esta unidad no guarda nada.
   *
   * ⚠️ La DESCARGA no pasa por acá: la hace `descargarArchivo`, que pone el mismo
   * `credentials: "include"` y además lee `Content-Disposition`. Mandarla por este
   * `pedir` obligaría a devolver el `Response` crudo y a repetir el armado del
   * `<a download>` que la primitiva ya tiene (`ADR-0386`).
   */
  const pedir = async (ruta: string, init: RequestInit): Promise<Response> => {
    let respuesta: Response
    try {
      respuesta = await fetch(`${raiz()}/${ruta}`, { ...init, credentials: "include" })
    } catch (causa) {
      throw Object.assign(
        errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0),
        { cause: causa },
      )
    }
    if (!respuesta.ok) {
      throw errorDelBorde(
        await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
        respuesta.status,
      )
    }
    return respuesta
  }

  return {
    async lista(consulta: ConsultaDeExportacion): Promise<PaginaDeExportacion> {
      const parametros = parametrosDelFiltro(consulta)
      parametros.set(PARAMETRO_PAGINA, String(consulta.page))
      parametros.set(PARAMETRO_TAMANO, String(consulta.pageSize))

      const respuesta = await pedir(`${RUTA_EXPORTACION}?${parametros.toString()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
      })

      const pagina = leerPagina(await respuesta.json())
      if (pagina === null) {
        throw errorDelBorde(
          "El servidor contestó la lista de exportación en una forma que no se entiende.",
          502,
        )
      }
      return pagina
    },

    async lotes(): Promise<readonly string[]> {
      const respuesta = await pedir(RUTA_LOTES, {
        method: "GET",
        headers: { Accept: "application/json" },
      })

      const lista = leerLotes(await respuesta.json())
      if (lista === null) {
        throw errorDelBorde(
          "El servidor contestó el listado de lotes en una forma que no se entiende.",
          502,
        )
      }
      return lista
    },

    /**
     * `GET exportacion/descarga` con el filtro APLICADO. El CSV se arma en el
     * momento, en el servidor, con todas las filas del filtro y en todas las
     * páginas: **el paginado no entra en la URL de la descarga**, y eso es lo que
     * hace imposible servir una página como si fuera el archivo (CA-76).
     *
     * ⛔ No se lee el cuerpo acá y no se arma ningún nombre: `descargarArchivo`
     * lanza si el servidor no responde `ok` o si no llega el nombre en
     * `Content-Disposition`, y la pantalla pinta su aviso por `onError`.
     */
    async descargar(filtro: FiltroDeExportacion): Promise<void> {
      const parametros = parametrosDelFiltro(filtro).toString()
      const consulta = parametros === "" ? "" : `?${parametros}`
      await descargarArchivo(`${raiz()}/${RUTA_DESCARGA}${consulta}`)
    },
  }
}
